import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Check, ChevronDown, ChevronLeft, ChevronRight, Lock, Pencil, AlertTriangle, RefreshCw, ExternalLink, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { BRL, MESES, STATUS_TX, fmtData } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SaldoConta from "@/components/SaldoConta";

const emptyTx = { tipo: "receber", categoria: "projeto", descricao: "", valor: "", data_vencimento: "", status: "previsto", cliente_id: "", membro: "", forma_pagamento: "", data_pagamento: "", observacoes: "", fornecedor: "" };
const emptyCt = { cliente_id: "", titulo: "", valor_mensal: "", valor_total: "", quantidade_parcelas: 1, parcelas_personalizadas: [], dia_vencimento: 10, inicio: "", fim: "", ativo: true, nf: "nao", formato: "mensal", frequencia_cobranca: "mensal", parcelas_por_mes: 4, primeiro_vencimento: "", renovacao_automatica: false, periodo_renovacao_meses: 0, aviso_vencimento_dias: 30, contrato_url: "", assinatura_status: "rascunho", aprovado_em: "", enviado_autentique_em: "", assinado_em: "", autentique_url: "", autentique_documento_id: "" };
const emptyCli = { empresa: "", nome: "", whatsapp: "", email: "", instagram: "", site: "" };
const emptyBaixa = { data_pagamento: "", valor_pago: "", forma_pagamento: "pix" };
const FORMATO_CT = { mensal: "Mensal", trimestral: "Trimestral", semestral: "Semestral", anual: "Anual", pontual: "Projeto único" };
const NF_CT = { nao: "Não emitir", mei: "MEI", netzer: "Netzer" };
const ASSINATURA_CT = {
  nao_informado: { nome: "Status não informado", classe: "text-[#71717A] border-[#27272A]" },
  rascunho: { nome: "Rascunho", classe: "text-[#A1A1AA] border-[#52525B]" },
  aprovado: { nome: "Aprovado", classe: "text-[#F59E0B] border-[#F59E0B]/35" },
  enviado_autentique: { nome: "Enviado ao Autentique", classe: "text-[#60A5FA] border-[#60A5FA]/35" },
  assinado: { nome: "Assinado", classe: "text-[#10B981] border-[#10B981]/35" },
};
const FIN_INICIO = "2026-09";
const dataLocalHoje = () => {
  const agora = new Date();
  return new Date(agora.getTime() - agora.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

export function buildContractExitForecast(contracts = [], clients = [], referenceDate = new Date(), months = 3) {
  const clientById = new Map(clients.map((client) => [client.id, client]));
  const base = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 1);
  const formatter = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });
  const groups = Array.from({ length: months }, (_, index) => {
    const date = new Date(base.getFullYear(), base.getMonth() + index, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const formatted = formatter.format(date);
    return {
      key,
      label: formatted.charAt(0).toUpperCase() + formatted.slice(1),
      horizonLabel: index === 0 ? "mês que vem" : `em ${index + 1} meses`,
      items: [],
      totalImpact: 0,
    };
  });
  const groupByKey = new Map(groups.map((group) => [group.key, group]));
  let missingEndDates = 0;

  contracts.forEach((contract) => {
    if (contract.ativo === false) return;
    const client = clientById.get(contract.cliente_id);
    let date = "";
    let kind = "contract";
    let impact = 0;
    let finalInstallmentPaid = false;
    let lastInstallmentDate = "";

    if (contract.formato === "pontual") {
      const installments = [...(contract.parcelamento?.parcelas || [])]
        .filter((installment) => installment.data_vencimento)
        .sort((a, b) => String(a.data_vencimento).localeCompare(String(b.data_vencimento)));
      const lastInstallment = installments.at(-1);
      lastInstallmentDate = lastInstallment?.data_vencimento || "";
      date = contract.fim || lastInstallmentDate;
      kind = contract.fim ? "project" : "installment";
      impact = lastInstallment
        ? Math.max(Number(lastInstallment.valor || 0) - Number(lastInstallment.valor_pago || 0), 0)
        : 0;
      finalInstallmentPaid = Boolean(lastInstallment && ["recebido", "pago"].includes(lastInstallment.status));
    } else {
      date = contract.fim || "";
      impact = Number(contract.valor_mensal || 0);
      if (!date) {
        missingEndDates += 1;
        return;
      }
    }

    const group = groupByKey.get(String(date).slice(0, 7));
    if (!group) return;
    const item = {
      id: contract.id,
      clientName: client?.empresa || client?.nome || contract.titulo || "Cliente sem nome",
      title: contract.titulo || "Contrato sem título",
      date,
      kind,
      impact,
      finalInstallmentPaid,
      lastInstallmentDate,
      automaticRenewal: contract.renovacao_automatica === true,
      contractUrl: contract.contrato_url || "",
      contract,
    };
    group.items.push(item);
    group.totalImpact += impact;
  });

  groups.forEach((group) => group.items.sort((a, b) => String(a.date).localeCompare(String(b.date))));
  return {
    groups,
    itemCount: groups.reduce((total, group) => total + group.items.length, 0),
    totalImpact: groups.reduce((total, group) => total + group.totalImpact, 0),
    missingEndDates,
  };
}

function TxTable({ lista, clientes, onToggle, onDelete, onEdit, tipo, vazio = "Nenhum lançamento no período." }) {
  const done = tipo === "receber" ? "recebido" : "pago";
  if (lista.length === 0) return <p className="text-sm text-[#71717A] py-4">{vazio}</p>;
  return (
    <div className="space-y-2">
      {lista.map((t) => {
        const st = STATUS_TX[t.status] || STATUS_TX.previsto;
        const cli = clientes.find((c) => c.id === t.cliente_id);
        const pago = Number(t.valor_pago || 0);
        const saldo = Math.max(Number(t.valor || 0) - pago, 0);
        return (
          <div key={t.id} data-testid={`tx-${t.id}`} className="studio-card px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm text-[#E4E4E0]">{t.descricao}</p>
              <p className="caption-label mt-0.5 normal-case tracking-normal">{t.categoria}{cli ? ` · ${cli.empresa}` : ""}{t.membro ? ` · ${t.membro}` : ""}</p>
            </div>
            <span className="font-mono text-xs text-[#71717A]">{fmtData(t.data_vencimento)}</span>
            <div className="text-right">
              <span className="font-mono text-sm text-[#F4F4F0]">{BRL(t.valor)}</span>
              {t.status === "parcial" ? <p className="font-mono text-[10px] text-[#F59E0B] mt-0.5">{BRL(pago)} {tipo === "receber" ? "recebido" : "pago"} · {BRL(saldo)} em aberto</p> : null}
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border" style={{ color: st.cor, borderColor: `${st.cor}55` }} data-testid={`tx-status-${t.id}`}>{t.status === "parcial" && t.tipo === "pagar" ? "Pago parcial" : st.nome}</span>
            {t.status !== done && (
              <button data-testid={`tx-baixa-${t.id}`} onClick={() => onToggle(t, done)} title={tipo === "receber" ? "Registrar recebimento" : "Registrar pagamento"}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 font-semibold text-xs">
                <Check size={13} />{t.status === "parcial" ? (tipo === "receber" ? "Receber saldo" : "Pagar saldo") : (tipo === "receber" ? "Receber" : "Pagar")}
              </button>
            )}
            <button data-testid={`tx-editar-${t.id}`} onClick={() => onEdit(t)} title="Editar lançamento" className="p-1.5 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={14} /></button>
            <button data-testid={`tx-excluir-${t.id}`} onClick={() => onDelete(t)} className="p-1.5 text-[#71717A] hover:text-[#F43F5E]"><Trash2 size={14} /></button>
          </div>
        );
      })}
    </div>
  );
}

export default function Financeiro() {
  const { user } = useAuth();
  const permitido = user?.role === "admin" || user?.fin_access;
  const agora = new Date();
  const [mesSel, setMesSel] = useState(`${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, "0")}`);
  const ano = parseInt(mesSel.slice(0, 4));
  const [resumo, setResumo] = useState(null);
  const [resumo2, setResumo2] = useState(null);
  const [periodo, setPeriodo] = useState("mes");
  const [propostas, setPropostas] = useState([]);
  const [transacoes, setTransacoes] = useState([]);
  const [transacoesVencidas, setTransacoesVencidas] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [dialogTx, setDialogTx] = useState(false);
  const [dialogCt, setDialogCt] = useState(false);
  const [formTx, setFormTx] = useState(emptyTx);
  const [formCt, setFormCt] = useState(emptyCt);
  const [tab, setTab] = useState("visao");
  const navigate = useNavigate();
  const [editTxId, setEditTxId] = useState(null);
  const [editCtId, setEditCtId] = useState(null);
  const [novoCliente, setNovoCliente] = useState(false);
  const [formCli, setFormCli] = useState(emptyCli);
  const [contratoAberto, setContratoAberto] = useState("");
  const [dialogBaixa, setDialogBaixa] = useState(false);
  const [transacaoBaixa, setTransacaoBaixa] = useState(null);
  const [formBaixa, setFormBaixa] = useState(emptyBaixa);
  const [modoBaixa, setModoBaixa] = useState("total");
  const [renovandoId, setRenovandoId] = useState("");
  const [saldoRevision, setSaldoRevision] = useState(0);
  const loadRevision = useRef(0);

  const load = () => {
    if (!permitido) return;
    const requestId = loadRevision.current + 1;
    loadRevision.current = requestId;
    const aplicarSeAtual = (setter) => (r) => {
      if (requestId === loadRevision.current) setter(r.data);
    };
    setResumo(null);
    setResumo2(null);
    setTransacoes([]);
    setSaldoRevision((value) => value + 1);
    api.get(`/financeiro/resumo?ano=${ano}`).then(aplicarSeAtual(setResumo)).catch(() => {});
    api.get(`/financeiro/resumo?ano=${ano + 1}`).then(aplicarSeAtual(setResumo2)).catch(() => {});
    api.get(`/financeiro/transacoes?mes=${mesSel}`).then(aplicarSeAtual(setTransacoes)).catch(() => {});
    api.get("/financeiro/transacoes?vencidas=true").then(aplicarSeAtual(setTransacoesVencidas)).catch(() => {});
    api.get("/financeiro/contratos").then(aplicarSeAtual(setContratos)).catch(() => {});
    api.get("/clientes").then(aplicarSeAtual(setClientes)).catch(() => {});
    api.get("/propostas").then(aplicarSeAtual(setPropostas)).catch(() => {});
  };
  useEffect(load, [ano, mesSel, permitido]);

  const salvarTx = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formTx, valor: parseFloat(formTx.valor) || 0 };
      if (editTxId) {
        await api.patch(`/financeiro/transacoes/${editTxId}`, payload);
        toast.success("Lançamento atualizado");
      } else {
        await api.post("/financeiro/transacoes", payload);
        toast.success("Lançamento criado");
      }
      setDialogTx(false);
      setEditTxId(null);
      setFormTx(emptyTx);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const salvarCt = async (e) => {
    e.preventDefault();
    try {
      let clienteId = formCt.cliente_id;
      if (novoCliente) {
        const res = await api.post("/clientes", { ...formCli, tipo: "recorrente", frequencia_captacao: "mensal", status: "ativo", entregas_mes: 0 });
        clienteId = res.data.id;
        toast.success("Cliente cadastrado");
      }
      const pontual = formCt.formato === "pontual";
      const payload = {
        ...formCt,
        cliente_id: clienteId,
        valor_mensal: parseFloat(pontual ? formCt.valor_total : formCt.valor_mensal) || 0,
        valor_total: parseFloat(formCt.valor_total) || 0,
        quantidade_parcelas: parseInt(formCt.quantidade_parcelas) || 1,
        parcelas_por_mes: parseInt(formCt.parcelas_por_mes) || 1,
        dia_vencimento: parseInt(formCt.dia_vencimento) || 10,
        renovacao_automatica: Boolean(formCt.renovacao_automatica),
        periodo_renovacao_meses: parseInt(formCt.periodo_renovacao_meses) || 0,
        aviso_vencimento_dias: parseInt(formCt.aviso_vencimento_dias) || 30,
      };
      if (editCtId) {
        await api.patch(`/financeiro/contratos/${editCtId}`, payload);
        toast.success("Contrato atualizado");
      } else {
        await api.post("/financeiro/contratos", payload);
        toast.success("Contrato criado com cobranças em A receber");
      }
      setDialogCt(false);
      setEditCtId(null);
      setFormCt(emptyCt);
      setNovoCliente(false);
      setFormCli(emptyCli);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const abrirEdicaoTx = (t) => {
    setEditTxId(t.id);
    setFormTx({ tipo: t.tipo, categoria: t.categoria, descricao: t.descricao, valor: String(t.valor), data_vencimento: t.data_vencimento, status: t.status, cliente_id: t.cliente_id || "", membro: t.membro || "", forma_pagamento: t.forma_pagamento || "", data_pagamento: t.data_pagamento || "", observacoes: t.observacoes || "", fornecedor: t.fornecedor || "" });
    setDialogTx(true);
  };

  const abrirEdicaoCt = (ct) => {
    setEditCtId(ct.id);
    setNovoCliente(false);
    setFormCt({
      cliente_id: ct.cliente_id, titulo: ct.titulo, valor_mensal: String(ct.valor_mensal || ""),
      valor_total: String(ct.valor_total || ""), quantidade_parcelas: ct.quantidade_parcelas || 1,
      parcelas_personalizadas: ct.parcelas_personalizadas || [],
      dia_vencimento: ct.dia_vencimento, inicio: ct.inicio || "", fim: ct.fim || "",
      ativo: ct.ativo !== false, nf: ct.nf || "nao", formato: ct.formato || "mensal",
      frequencia_cobranca: ct.frequencia_cobranca || "mensal", parcelas_por_mes: ct.parcelas_por_mes || 4,
      primeiro_vencimento: ct.primeiro_vencimento || "",
      renovacao_automatica: ct.renovacao_automatica === true,
      periodo_renovacao_meses: ct.periodo_renovacao_meses || 0,
      aviso_vencimento_dias: ct.aviso_vencimento_dias || 30,
      contrato_url: ct.contrato_url || "",
      assinatura_status: ct.assinatura_status || "nao_informado",
      aprovado_em: ct.aprovado_em || "",
      enviado_autentique_em: ct.enviado_autentique_em || "",
      assinado_em: ct.assinado_em || "",
      autentique_url: ct.autentique_url || "",
      autentique_documento_id: ct.autentique_documento_id || "",
    });
    setDialogCt(true);
  };

  const baixa = (t, status) => {
    const saldo = Math.max(Number(t.valor || 0) - Number(t.valor_pago || 0), 0);
    setTransacaoBaixa({ ...t, status_baixa: status });
    setFormBaixa({
      data_pagamento: dataLocalHoje(),
      valor_pago: String(saldo || ""),
      forma_pagamento: (t.forma_pagamento || "pix").toLowerCase(),
    });
    setModoBaixa("total");
    setDialogBaixa(true);
  };

  const confirmarBaixa = async (e) => {
    e.preventDefault();
    if (!transacaoBaixa) return;
    const valorPago = parseFloat(formBaixa.valor_pago) || 0;
    try {
      await api.post(`/financeiro/transacoes/${transacaoBaixa.id}/baixa`, {
        valor: valorPago,
        data_pagamento: formBaixa.data_pagamento,
        forma_pagamento: formBaixa.forma_pagamento,
      });
      const saldoAnterior = Math.max(Number(transacaoBaixa.valor || 0) - Number(transacaoBaixa.valor_pago || 0), 0);
      const parcial = valorPago < saldoAnterior;
      const recebimento = transacaoBaixa.tipo === "receber";
      toast.success(parcial ? `${recebimento ? "Recebimento" : "Pagamento"} parcial registrado` : "Baixa realizada");
      setDialogBaixa(false);
      setTransacaoBaixa(null);
      setFormBaixa(emptyBaixa);
      setModoBaixa("total");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const confirmarBaixaNoVencimento = async () => {
    if (!transacaoBaixa) return;
    const valorPago = parseFloat(formBaixa.valor_pago) || 0;
    try {
      await api.post(`/financeiro/transacoes/${transacaoBaixa.id}/baixa`, {
        valor: valorPago,
        data_pagamento: transacaoBaixa.data_vencimento,
        forma_pagamento: formBaixa.forma_pagamento,
      });
      const saldoAnterior = Math.max(Number(transacaoBaixa.valor || 0) - Number(transacaoBaixa.valor_pago || 0), 0);
      const parcial = valorPago < saldoAnterior;
      const recebimento = transacaoBaixa.tipo === "receber";
      toast.success(parcial ? `${recebimento ? "Recebimento" : "Pagamento"} parcial registrado no vencimento` : "Baixa realizada na data do vencimento");
      setDialogBaixa(false);
      setTransacaoBaixa(null);
      setFormBaixa(emptyBaixa);
      setModoBaixa("total");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const corrigirPagamentoParaVencimento = async () => {
    if (!editTxId) return;
    try {
      await api.post(`/financeiro/transacoes/${editTxId}/pagamento-no-vencimento`);
      toast.success("Data do pagamento corrigida para o vencimento");
      setDialogTx(false);
      setEditTxId(null);
      setFormTx(emptyTx);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const excluirTx = async (t) => {
    if (!window.confirm(`Excluir a cobrança “${t.descricao}”?`)) return;
    await api.delete(`/financeiro/transacoes/${t.id}`);
    toast.success("Lançamento removido");
    load();
  };
  const excluirCt = async (c) => { await api.delete(`/financeiro/contratos/${c.id}`); toast.success("Contrato removido"); load(); };
  const renovarCt = async (c) => {
    const meses = Number(c.periodo_renovacao_meses || 1);
    if (!window.confirm(`Renovar “${c.titulo}” por mais ${meses} mês(es)? As cobranças futuras serão atualizadas automaticamente.`)) return;
    setRenovandoId(c.id);
    try {
      const { data } = await api.post(`/financeiro/contratos/${c.id}/renovar`, { meses });
      toast.success(`Contrato renovado até ${fmtData(data.fim)}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setRenovandoId(""); }
  };
  const alternarRenovacaoAutomatica = async (c) => {
    if (!c.fim) {
      toast.error("Defina uma data final para ativar a renovação automática");
      return;
    }
    setRenovandoId(c.id);
    try {
      await api.patch(`/financeiro/contratos/${c.id}`, {
        renovacao_automatica: !c.renovacao_automatica,
        periodo_renovacao_meses: c.periodo_renovacao_meses || 1,
      });
      toast.success(!c.renovacao_automatica ? "Renovação automática ativada" : "Renovação automática desativada");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setRenovandoId(""); }
  };

  const chartData = useMemo(() => (resumo?.meses || []).filter((m) => m.mes >= FIN_INICIO).map((m) => ({
    name: MESES[parseInt(m.mes.slice(5)) - 1], Receita: m.receita_prevista, Custo: m.custo_previsto, Lucro: m.lucro_previsto,
  })), [resumo]);

  const mesIdx = parseInt(mesSel.split("-")[1]) - 1;
  const mesData = resumo?.meses?.[mesIdx];
  const aReceber = transacoes.filter((t) => {
    if (t.tipo !== "receber" || t.status === "recebido") return false;
    const saldo = t.saldo_aberto ?? (Number(t.valor || 0) - Number(t.valor_pago || 0));
    return Number(saldo) > 0;
  });
  const recebidos = transacoes
    .filter((t) => t.tipo === "receber" && t.status === "recebido")
    .sort((a, b) => String(b.data_pagamento || b.data_vencimento || "").localeCompare(String(a.data_pagamento || a.data_vencimento || "")));
  const totalRecebido = recebidos.reduce((total, t) => total + Number(t.valor_pago ?? t.valor ?? 0), 0);
  const aPagar = transacoes.filter((t) => t.tipo === "pagar");
  const equipe = aPagar.filter((t) => t.categoria === "equipe");
  const contratosVencendo = contratos.filter((c) => c.status_vencimento === "vence_em_breve" || c.status_vencimento === "vencido");
  const exitForecast = useMemo(
    () => buildContractExitForecast(contratos, clientes, new Date(), 3),
    [contratos, clientes],
  );

  const mudarMesSel = (delta) => {
    const [a, m] = mesSel.split("-").map(Number);
    const d = new Date(a, m - 1 + delta, 1);
    setMesSel(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const totais = resumo?.totais;

  if (!permitido)
    return (
      <div className="studio-card p-12 text-center max-w-lg mx-auto mt-10" data-testid="financeiro-restrito">
        <Lock size={32} className="text-[#8CC63F] mx-auto mb-5" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-[#F4F4F0]">Área <span className="gold-italic">restrita</span></h2>
        <p className="text-sm text-[#A1A1AA] mt-3">Os números financeiros e comerciais do estúdio são visíveis apenas para o administrador e perfis autorizados em <span className="text-[#E4E4E0]">Acessos</span>.</p>
      </div>
    );
  const somarMeses = (lista) => lista.reduce((acc, m) => ({
    receita_prevista: acc.receita_prevista + m.receita_prevista,
    custo_previsto: acc.custo_previsto + m.custo_previsto,
    lucro_previsto: acc.lucro_previsto + m.lucro_previsto,
    receita: acc.receita + m.receita,
    custo: acc.custo + m.custo,
    lucro: acc.lucro + m.lucro,
    recorrente_prevista: acc.recorrente_prevista + (m.recorrente_prevista || 0),
    pontual_prevista: acc.pontual_prevista + (m.pontual_prevista || 0),
    vencidos: acc.vencidos + (m.vencidos || 0),
    vencidos_valor: acc.vencidos_valor + (m.vencidos_valor || 0),
    imposto_min_previsto: acc.imposto_min_previsto + (m.imposto_min_previsto || 0),
    imposto_max_previsto: acc.imposto_max_previsto + (m.imposto_max_previsto || 0),
  }), { receita_prevista: 0, custo_previsto: 0, lucro_previsto: 0, receita: 0, custo: 0, lucro: 0, recorrente_prevista: 0, pontual_prevista: 0, vencidos: 0, vencidos_valor: 0, imposto_min_previsto: 0, imposto_max_previsto: 0 });

  const periodoDados = (() => {
    if (!resumo || !mesData) return null;
    if (periodo === "ano") return somarMeses(resumo.meses);
    if (periodo === "trimestre") return somarMeses(resumo.meses.filter((_, i) => Math.abs(i - mesIdx) <= 1));
    return mesData;
  })();

  const projecao12 = (() => {
    if (!resumo) return [];
    const all = [...resumo.meses, ...(resumo2?.meses || [])];
    const agoraKey = new Date().toISOString().slice(0, 7);
    const start = Math.max(0, all.findIndex((m) => m.mes === agoraKey));
    return all.slice(start, start + 12).map((m) => ({
      name: `${MESES[parseInt(m.mes.slice(5)) - 1]}/${m.mes.slice(2, 4)}`,
      Receita: m.receita_prevista, Custo: m.custo_previsto, Lucro: m.lucro_previsto,
    }));
  })();

  const periodoLabel = periodo === "mes" ? MESES[mesIdx] : periodo === "trimestre" ? "trimestre" : String(ano);
  const impostoSub = periodo === "mes" && periodoDados?.imposto_regra_aplicavel === false
    ? "regra automática inicia em nov/2026 · outubro considera apenas NFs emitidas"
    : "estimativa de 6% a 12% · validar com a contabilidade";
  const cards = periodoDados ? [
    { id: "receita", label: `Receita prevista - ${periodoLabel}`, valor: periodoDados.receita_prevista, sub: `recebido ${BRL(periodoDados.receita)}`, to: "receber" },
    { id: "custo", label: `Custos previstos - ${periodoLabel}`, valor: periodoDados.custo_previsto, sub: `pago ${BRL(periodoDados.custo)}`, to: "pagar" },
    { id: "impostos", label: `Reserva de impostos - ${periodoLabel}`, valor: periodoDados.imposto_min_previsto || 0, valorMax: periodoDados.imposto_max_previsto || 0, sub: impostoSub, to: "projecoes" },
    { id: "lucro", label: `Lucro previsto - ${periodoLabel}`, valor: periodoDados.receita_prevista - periodoDados.custo_previsto, sub: `realizado ${BRL(periodoDados.lucro)}`, neg: periodoDados.receita_prevista - periodoDados.custo_previsto < 0, to: "movimentacoes" },
    { id: "recorrente", label: "Receita recorrente", valor: periodoDados.recorrente_prevista, sub: `pontual ${BRL(periodoDados.pontual_prevista)}`, to: "contratos" },
    { id: "vencidos", label: "Recebimentos vencidos", valor: resumo.vencidos_atuais_valor || 0, sub: `${resumo.vencidos_atuais || 0} cobrança(s) · todos os meses`, neg: (resumo.vencidos_atuais || 0) > 0, to: "vencidos" },
    { id: "mrr", label: `Receita recorrente (MRR) - ${MESES[mesIdx]}`, valor: mesData.mrr || 0, sub: "contratos vigentes no mês", to: "contratos" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="caption-label mb-2">04 // Financeiro</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight">Clareza <span className="gold-italic">financeira</span></h1>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button data-testid="novo-contrato-button" onClick={() => { setEditCtId(null); setFormCt(emptyCt); setNovoCliente(false); setFormCli(emptyCli); setDialogCt(true); }} variant="outline" className="flex-1 border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10 rounded-full sm:flex-none">
            <Plus size={15} className="mr-1" /> Contrato
          </Button>
          <Button data-testid="novo-lancamento-button" onClick={() => { setEditTxId(null); setFormTx(emptyTx); setDialogTx(true); }} className="flex-1 bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold sm:flex-none">
            <Plus size={15} className="mr-1" /> Lançamento
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="fin-mes-anterior" onClick={() => mudarMesSel(-1)} className="p-2 text-[#71717A] hover:text-[#8CC63F]"><ChevronLeft size={18} /></button>
        <span className="font-serif text-xl text-[#F4F4F0] capitalize">{MESES[mesIdx]} <span className="font-mono text-sm text-[#71717A]">{mesSel.split("-")[0]}</span></span>
        <button data-testid="fin-mes-proximo" onClick={() => mudarMesSel(1)} className="p-2 text-[#71717A] hover:text-[#8CC63F]"><ChevronRight size={18} /></button>
        <div className="hidden flex-1 sm:block" />
        <div className="order-last flex w-full overflow-hidden rounded-full border border-[#27272A] sm:order-none sm:w-auto">
          {[["mes", "Mês"], ["trimestre", "Trimestre"], ["ano", "Ano"]].map(([k, l]) => (
            <button key={k} data-testid={`periodo-${k}`} onClick={() => setPeriodo(k)}
              className={`min-h-10 flex-1 px-3.5 py-1.5 text-xs font-mono uppercase tracking-wide transition-colors sm:flex-none ${periodo === k ? "bg-[#8CC63F]/15 text-[#8CC63F]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}>
              {l}
            </button>
          ))}
        </div>
        <Select value={String(ano)} onValueChange={(v) => setMesSel(`${v}-${mesSel.slice(5, 7)}`)}>
          <SelectTrigger data-testid="fin-ano-select" className="ml-auto w-24 bg-[#141418] border-[#27272A] text-[#E4E4E0] sm:ml-0 sm:w-28"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
            {[agora.getFullYear() - 1, agora.getFullYear(), agora.getFullYear() + 1].map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {contratosVencendo.length > 0 && (
        <section className="rounded-xl border border-[#F59E0B]/35 bg-[#F59E0B]/5 p-4 sm:p-5" data-testid="alertas-vencimento-contratos">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-[#F59E0B]" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold text-[#F4F4F0]">Contratos que precisam de atenção</h2>
              <p className="mt-1 text-xs text-[#A1A1AA]">Renove agora ou ative a renovação automática para manter o cliente e as cobranças futuras na carteira.</p>
              <div className="mt-4 space-y-2">
                {contratosVencendo.map((c) => {
                  const cli = clientes.find((x) => x.id === c.cliente_id);
                  const vencido = c.status_vencimento === "vencido";
                  return (
                    <div key={c.id} className="flex flex-col gap-3 rounded-lg border border-[#F59E0B]/20 bg-[#141418] px-4 py-3 sm:flex-row sm:items-center">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#E4E4E0]">Atenção: o contrato de {cli?.empresa || c.titulo} {vencido ? `venceu há ${Math.abs(c.dias_para_vencer)} dia(s)` : `termina em ${c.dias_para_vencer} dia(s)`}.</p>
                        <p className="caption-label mt-1 normal-case tracking-normal">Término em {fmtData(c.fim)} · renovação por {c.periodo_renovacao_meses} mês(es)</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" disabled={renovandoId === c.id} onClick={() => alternarRenovacaoAutomatica(c)} className={`rounded-full ${c.renovacao_automatica ? "border-[#10B981]/40 text-[#10B981]" : "border-[#F59E0B]/40 text-[#F59E0B]"}`}>
                          <RefreshCw size={14} className="mr-1.5" />{c.renovacao_automatica ? "Automática ativa" : "Ativar automática"}
                        </Button>
                        <Button type="button" disabled={renovandoId === c.id} onClick={() => renovarCt(c)} className="rounded-full bg-[#8CC63F] font-semibold text-[#09090B] hover:bg-[#A9DD6B]">
                          {renovandoId === c.id ? "Renovando…" : "Renovar agora"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {resumo && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <button key={c.id} data-testid={`fin-card-${c.id}`} onClick={() => setTab(c.to)} title="Ver a origem deste número"
              className="studio-card studio-card-hover p-5 text-left cursor-pointer">
              <p className="caption-label">{c.label}</p>
              <p className={`font-mono text-xl sm:text-2xl font-semibold mt-3 tracking-tight ${c.neg ? "text-[#F43F5E]" : "text-[#F4F4F0]"}`}>{c.valorMax != null ? `${BRL(c.valor)} – ${BRL(c.valorMax)}` : BRL(c.valor)}</p>
              <p className="font-mono text-[10px] text-[#71717A] mt-2 uppercase">{c.sub}</p>
            </button>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto w-full border border-[#27272A] bg-[#141418]">
          <TabsTrigger value="visao" data-testid="tab-visao-geral">Visão geral</TabsTrigger>
          <TabsTrigger value="saldo" data-testid="tab-saldo-conta">Saldo em conta</TabsTrigger>
          <TabsTrigger value="projecoes" data-testid="tab-projecoes">Projeções 12m</TabsTrigger>
          <TabsTrigger value="receber" data-testid="tab-a-receber">A receber ({aReceber.length})</TabsTrigger>
          <TabsTrigger value="recebidos" data-testid="tab-recebidos">Recebidos ({recebidos.length})</TabsTrigger>
          <TabsTrigger value="vencidos" data-testid="tab-vencidos" className="data-[state=active]:text-[#F43F5E]">Em atraso ({transacoesVencidas.length})</TabsTrigger>
          <TabsTrigger value="pagar" data-testid="tab-a-pagar">A pagar ({aPagar.length})</TabsTrigger>
          <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes">Movimentações</TabsTrigger>
          <TabsTrigger value="contratos" data-testid="tab-contratos">Contratos ({contratos.length})</TabsTrigger>
          <TabsTrigger value="equipe" data-testid="tab-equipe">Equipe ({equipe.length})</TabsTrigger>
          <TabsTrigger value="comercial" data-testid="tab-comercial-fin">Comercial</TabsTrigger>
        </TabsList>

        <TabsContent value="saldo" className="mt-6">
          <SaldoConta mes={mesSel} revision={saldoRevision} clientes={clientes} onEdit={abrirEdicaoTx} />
        </TabsContent>

        <TabsContent value="visao" className="mt-6 space-y-6">
          <section className="studio-card p-5 lg:p-6" data-testid="contract-exit-forecast">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="rounded-xl border border-[#8CC63F]/25 bg-[#8CC63F]/10 p-2.5 text-[#8CC63F]">
                  <CalendarClock size={20} />
                </span>
                <div>
                  <p className="caption-label">Proteção de receita</p>
                  <h2 className="mt-1 font-serif text-2xl text-[#F4F4F0]">Encerramentos nos próximos 3 meses</h2>
                  <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[#A1A1AA]">Contratos com data final e últimas parcelas de projetos, organizados para antecipar renovação, retenção e reposição de receita.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <span className="rounded-full border border-[#27272A] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[#A1A1AA]">{exitForecast.itemCount} encerramento(s)</span>
                <Button type="button" variant="outline" onClick={() => setTab("contratos")} className="rounded-full border-[#8CC63F]/35 text-[#8CC63F] hover:bg-[#8CC63F]/10">Revisar contratos</Button>
              </div>
            </div>

            <div className="mt-5 grid gap-3 lg:grid-cols-3">
              {exitForecast.groups.map((group, index) => (
                <div key={group.key} data-testid={`contract-exit-month-${group.key}`} className={`rounded-xl border p-4 ${index === 0 ? "border-[#F43F5E]/30 bg-[#F43F5E]/5" : index === 1 ? "border-[#F59E0B]/25 bg-[#F59E0B]/5" : "border-[#27272A] bg-[#101014]"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-[#F4F4F0]">{group.label}</p>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-[#71717A]">{group.horizonLabel}</p>
                    </div>
                    <span className="font-mono text-xs text-[#A1A1AA]">{group.items.length}</span>
                  </div>

                  <div className="mt-4 space-y-3">
                    {group.items.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-[#27272A] px-3 py-5 text-center text-xs text-[#71717A]">Nenhum encerramento previsto.</p>
                    ) : group.items.map((item) => (
                      <div key={item.id} className="rounded-lg border border-[#27272A] bg-[#141418] p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#E4E4E0]">{item.clientName}</p>
                            <p className="mt-0.5 truncate text-[11px] text-[#71717A]">{item.title}</p>
                          </div>
                          <span className={`shrink-0 rounded-full border px-2 py-1 font-mono text-[9px] uppercase tracking-wider ${item.kind === "contract" ? "border-[#F43F5E]/30 text-[#F43F5E]" : "border-[#F59E0B]/30 text-[#F59E0B]"}`}>
                            {item.kind === "contract" ? "Contrato encerra" : item.kind === "project" ? "Projeto encerra" : "Última parcela"}
                          </span>
                        </div>
                        <p className="mt-3 font-mono text-xs text-[#A1A1AA]">{item.kind === "project" ? `Projeto até ${fmtData(item.date)}` : fmtData(item.date)}</p>
                        {item.kind === "project" && item.lastInstallmentDate && (
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-[#A1A1AA]">Última parcela em {fmtData(item.lastInstallmentDate)}</p>
                        )}
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-[#71717A]">
                          {item.kind === "contract"
                            ? `MRR em risco ${BRL(item.impact)}`
                            : item.finalInstallmentPaid
                              ? "Parcela final já recebida"
                              : item.impact > 0
                                ? `Saldo final ${BRL(item.impact)}`
                                : "Sem saldo de parcela vinculado"}
                        </p>
                        {item.automaticRenewal && <p className="mt-2 text-[10px] font-semibold text-[#10B981]">Renovação automática ativa</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={() => abrirEdicaoCt(item.contract)} className="font-mono text-[10px] uppercase tracking-wider text-[#8CC63F] hover:text-[#A9DD6B]">Abrir cadastro</button>
                          {item.contractUrl ? (
                            <a href={item.contractUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[#F4F4F0]">Contrato <ExternalLink size={11} /></a>
                          ) : (
                            <span className="font-mono text-[10px] uppercase tracking-wider text-[#52525B]">Sem link do contrato</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 border-t border-[#27272A] pt-3">
                    <p className="caption-label">Valores associados</p>
                    <p className="mt-1 font-mono text-sm text-[#F4F4F0]">{BRL(group.totalImpact)}</p>
                  </div>
                </div>
              ))}
            </div>

            {exitForecast.missingEndDates > 0 && (
              <p className="mt-4 rounded-lg border border-[#27272A] bg-[#101014] px-3 py-2 text-xs text-[#A1A1AA]">
                {exitForecast.missingEndDates} contrato(s) recorrente(s) ativo(s) estão sem data final e, por isso, não entram nesta previsão.
              </p>
            )}
          </section>

          <div className="studio-card p-5 lg:p-6">
            <p className="caption-label mb-4">Receita x Custos · previsão {ano}</p>
            <div className="h-72" data-testid="chart-receita-custo">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="var(--studio-border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "var(--studio-elevated)", border: "1px solid var(--studio-border)", color: "var(--studio-text)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => BRL(v)} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
                  <Bar dataKey="Receita" fill="var(--studio-primary)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Custo" fill="var(--studio-border-strong)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="studio-card p-5 lg:p-6">
            <p className="caption-label mb-4">Lucro previsto · {ano} {totais && <span className="text-[#8CC63F]">· ano {BRL(totais.lucro_previsto_ano)}</span>}</p>
            <div className="h-56" data-testid="chart-lucro">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="var(--studio-border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "var(--studio-elevated)", border: "1px solid var(--studio-border)", color: "var(--studio-text)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => BRL(v)} />
                  <Line type="monotone" dataKey="Lucro" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="projecoes" className="mt-6" data-testid="projecoes-content">
          <div className="studio-card p-5 lg:p-6">
            <p className="caption-label mb-1">Projeção dos próximos 12 meses</p>
            <p className="text-xs text-[#71717A] mb-4">Receitas recorrentes projetadas automaticamente a partir dos contratos ativos + lançamentos previstos. Não é dinheiro recebido — é previsão.</p>
            <div className="h-80" data-testid="chart-projecao">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={projecao12}>
                  <CartesianGrid stroke="var(--studio-border)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="var(--studio-muted)" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "var(--studio-elevated)", border: "1px solid var(--studio-border)", color: "var(--studio-text)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => BRL(v)} />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
                  <Area type="monotone" dataKey="Receita" stroke="var(--studio-primary)" fill="var(--studio-primary)" fillOpacity={0.14} strokeWidth={2} />
                  <Area type="monotone" dataKey="Custo" stroke="var(--studio-muted)" fill="var(--studio-border)" fillOpacity={0.4} strokeWidth={2} />
                  <Area type="monotone" dataKey="Lucro" stroke="#10B981" fill="#10B98122" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="receber" className="mt-6">
          <TxTable lista={aReceber} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="receber" vazio="Nenhuma cobrança em aberto neste período." />
        </TabsContent>

        <TabsContent value="recebidos" className="mt-6 space-y-4" data-testid="recebidos-content">
          <div className="rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 px-4 py-3">
            <p className="text-sm font-semibold text-[#10B981]">Recebimentos confirmados no período</p>
            <p className="mt-1 text-xs text-[#A1A1AA]">{recebidos.length} pagamento(s) quitado(s) · total recebido {BRL(totalRecebido)}. Baixas parciais permanecem em “A receber” até a quitação do saldo.</p>
          </div>
          <TxTable lista={recebidos} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="receber" vazio="Nenhum recebimento confirmado neste período." />
        </TabsContent>

        <TabsContent value="vencidos" className="mt-6 space-y-4" data-testid="vencidos-content">
          <div className="rounded-xl border border-[#F43F5E]/30 bg-[#F43F5E]/5 px-4 py-3">
            <p className="text-sm font-semibold text-[#F43F5E]">Contas em atraso de todos os meses</p>
            <p className="text-xs text-[#A1A1AA] mt-1">A lista considera o saldo ainda não recebido de qualquer cobrança com vencimento anterior a hoje.</p>
          </div>
          <TxTable lista={transacoesVencidas} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="receber" />
        </TabsContent>

        <TabsContent value="pagar" className="mt-6">
          <TxTable lista={aPagar} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="pagar" />
        </TabsContent>

        <TabsContent value="movimentacoes" className="mt-6" data-testid="movimentacoes-content">
          <p className="caption-label mb-3">Dinheiro real do mês — entradas e saídas efetivadas</p>
          <TxTable lista={transacoes.filter((t) => ["recebido", "pago", "parcial"].includes(t.status))} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="receber" />
          {transacoes.filter((t) => ["recebido", "pago", "parcial"].includes(t.status)).length === 0 && <p className="text-sm text-[#71717A]">Nenhuma movimentação realizada neste mês ainda.</p>}
        </TabsContent>

        <TabsContent value="contratos" className="mt-6 space-y-3">
          <div className="rounded-xl border border-[#8CC63F]/25 bg-[#8CC63F]/5 px-4 py-3" data-testid="contratos-assinatura-resumo">
            <p className="text-sm font-semibold text-[#E4E4E0]">Comercial, Contratos e Financeiro no mesmo registro</p>
            <p className="mt-1 text-xs text-[#A1A1AA]">Venda ganha inicia o contrato como aprovado. O envio ao Autentique e a assinatura atualizam este mesmo cadastro, sem criar outra venda ou cobrança.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {["aprovado", "enviado_autentique", "assinado"].map((status) => (
                <span key={status} className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${ASSINATURA_CT[status].classe}`}>
                  {ASSINATURA_CT[status].nome}: {contratos.filter((c) => c.assinatura_status === status).length}
                </span>
              ))}
            </div>
          </div>
          {contratos.length === 0 && <p className="text-sm text-[#71717A]">Nenhum contrato recorrente. Contratos alimentam a previsão de faturamento automaticamente.</p>}
          {contratos.map((c) => {
            const cli = clientes.find((x) => x.id === c.cliente_id);
            const parcelamento = c.parcelamento;
            const aberto = contratoAberto === c.id;
            const proximaIndice = parcelamento?.parcelas?.findIndex((p) => !["recebido", "pago"].includes(p.status)) ?? -1;
            const contratoLink = /^https?:\/\//i.test(c.contrato_url || "") ? c.contrato_url : "";
            const autentiqueLink = /^https?:\/\//i.test(c.autentique_url || "") ? c.autentique_url : "";
            const assinatura = ASSINATURA_CT[c.assinatura_status] || ASSINATURA_CT.nao_informado;
            return (
              <div key={c.id} data-testid={`contrato-${c.id}`} className="studio-card overflow-hidden">
                <div className="px-5 py-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-medium text-[#E4E4E0]">{c.titulo}</p>
                    <p className="caption-label mt-1 normal-case tracking-normal">{cli?.empresa || "—"} · {FORMATO_CT[c.formato] || "Mensal"} · cobrança {c.frequencia_cobranca === "semanal" ? "semanal" : "mensal"} · desde {fmtData(c.inicio)}{c.fim ? ` até ${fmtData(c.fim)}` : " · indeterminado"}</p>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${assinatura.classe}`} data-testid={`contrato-assinatura-${c.id}`}>{assinatura.nome}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${c.nf && c.nf !== "nao" ? "text-[#8CC63F] border-[#8CC63F]/30" : "text-[#71717A] border-[#27272A]"}`} data-testid={`contrato-nf-${c.id}`}>{NF_CT[c.nf] ? `NF: ${NF_CT[c.nf]}` : "Sem NF"}</span>
                  <span className="font-mono text-sm text-[#8CC63F]">{c.formato === "pontual" ? `${BRL(c.valor_total || c.valor_mensal)} · ${(c.parcelas_personalizadas || []).length ? `${c.parcelas_personalizadas.length} parcelas variáveis` : `${c.quantidade_parcelas || 1}x de ${BRL(c.valor_parcela || c.valor_mensal)}`}` : `${BRL(c.valor_mensal)}/mês`}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${c.ativo ? "text-[#10B981] border-[#10B981]/30" : "text-[#71717A] border-[#27272A]"}`}>{c.ativo ? "ativo" : "inativo"}</span>
                  {autentiqueLink && (
                    <a data-testid={`contrato-autentique-link-${c.id}`} href={autentiqueLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 rounded-full border border-[#60A5FA]/35 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#60A5FA] hover:bg-[#60A5FA]/10">
                      <ExternalLink size={12} />Autentique
                    </a>
                  )}
                  {contratoLink && (
                    <a data-testid={`contrato-link-${c.id}`} href={contratoLink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 rounded-full border border-[#8CC63F]/35 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8CC63F] hover:bg-[#8CC63F]/10">
                      <ExternalLink size={12} />Abrir contrato
                    </a>
                  )}
                  {c.formato !== "pontual" && c.fim && (
                    <>
                      <button type="button" data-testid={`contrato-auto-renovacao-${c.id}`} disabled={renovandoId === c.id} onClick={() => alternarRenovacaoAutomatica(c)} title="Ativar ou desativar renovação automática" className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${c.renovacao_automatica ? "border-[#10B981]/35 text-[#10B981]" : "border-[#27272A] text-[#71717A] hover:border-[#8CC63F]/35 hover:text-[#8CC63F]"}`}>
                        <RefreshCw size={12} />{c.renovacao_automatica ? "auto ativa" : "auto inativa"}
                      </button>
                      <button type="button" data-testid={`contrato-renovar-${c.id}`} disabled={renovandoId === c.id} onClick={() => renovarCt(c)} className="rounded-full border border-[#8CC63F]/35 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8CC63F] hover:bg-[#8CC63F]/10">
                        {renovandoId === c.id ? "Renovando…" : "Renovar"}
                      </button>
                    </>
                  )}
                  {parcelamento?.parcelas?.length > 0 && (
                    <button type="button" data-testid={`contrato-parcelas-${c.id}`} onClick={() => setContratoAberto(aberto ? "" : c.id)} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:text-[#8CC63F]">
                      {aberto ? "Ocultar" : "Ver cobranças"}<ChevronDown size={14} className={`transition-transform ${aberto ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  <button data-testid={`contrato-editar-${c.id}`} onClick={() => abrirEdicaoCt(c)} title="Editar contrato" className="p-1.5 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={14} /></button>
                  <button data-testid={`contrato-excluir-${c.id}`} onClick={() => excluirCt(c)} className="p-1.5 text-[#71717A] hover:text-[#F43F5E]"><Trash2 size={14} /></button>
                </div>
                {parcelamento?.parcelas?.length > 0 && (
                  <div className="px-5 pb-4" data-testid={`parcelamento-resumo-${c.id}`}>
                    <div className="h-1.5 rounded-full bg-[#27272A] overflow-hidden">
                      <div className="h-full rounded-full bg-[#8CC63F] transition-all" style={{ width: `${Math.min(parcelamento.progresso || 0, 100)}%` }} />
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div><p className="caption-label">Cobranças quitadas</p><p className="font-mono text-sm text-[#F4F4F0] mt-1">{parcelamento.parcelas_pagas} de {parcelamento.parcelas.length}</p></div>
                      <div><p className="caption-label">Recebido</p><p className="font-mono text-sm text-[#10B981] mt-1">{BRL(parcelamento.total_recebido)}</p></div>
                      <div><p className="caption-label">Saldo restante</p><p className="font-mono text-sm text-[#F4F4F0] mt-1">{BRL(parcelamento.saldo_restante)}</p></div>
                      <div><p className="caption-label">Próximo vencimento</p><p className="font-mono text-sm text-[#F4F4F0] mt-1">{parcelamento.proxima_parcela ? `${c.formato === "pontual" ? (parcelamento.proxima_parcela.parcela_numero || proximaIndice + 1) : proximaIndice + 1}/${c.formato === "pontual" ? (parcelamento.proxima_parcela.total_parcelas || parcelamento.total_parcelas) : parcelamento.parcelas.length} · ${fmtData(parcelamento.proxima_parcela.data_vencimento)}` : "Quitado"}</p></div>
                    </div>
                  </div>
                )}
                {aberto && parcelamento && (
                  <div className="border-t border-[#27272A] bg-[#101014] px-5 py-4 space-y-2" data-testid={`parcelamento-lista-${c.id}`}>
                    {parcelamento.parcelas.map((p, indice) => {
                      const recebida = ["recebido", "pago"].includes(p.status);
                      const parcial = p.status === "parcial";
                      const numeroExibido = c.formato === "pontual" ? (p.parcela_numero || indice + 1) : indice + 1;
                      const totalExibido = c.formato === "pontual" ? (p.total_parcelas || parcelamento.parcelas.length) : parcelamento.parcelas.length;
                      return (
                        <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#27272A] px-3 py-2.5">
                          <span className="font-mono text-xs text-[#A1A1AA] w-14">{numeroExibido}/{totalExibido}</span>
                          <span className="font-mono text-xs text-[#A1A1AA]">{fmtData(p.data_vencimento)}</span>
                          <span className="font-mono text-sm text-[#F4F4F0] flex-1">{BRL(p.valor)}</span>
                          {p.data_pagamento && <span className="font-mono text-[10px] text-[#71717A]">baixada em {fmtData(p.data_pagamento)}</span>}
                          <span className={`font-mono text-[10px] uppercase tracking-widest ${recebida ? "text-[#10B981]" : parcial ? "text-[#F59E0B]" : "text-[#A1A1AA]"}`}>{recebida ? "recebida" : parcial ? `parcial · ${BRL(p.valor_pago)} recebido · ${BRL(Math.max(p.valor - p.valor_pago, 0))} restante` : "pendente"}</span>
                          {!recebida && <button type="button" onClick={() => baixa(p, "recebido")} className="rounded-full border border-[#10B981]/40 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#10B981] hover:bg-[#10B981]/10">{parcial ? "Receber saldo" : "Dar baixa"}</button>}
                          <button type="button" onClick={() => abrirEdicaoTx(p)} title="Editar cobrança" aria-label="Editar cobrança" className="p-1.5 text-[#71717A] hover:text-[#8CC63F]"><Pencil size={14} /></button>
                          <button type="button" onClick={() => excluirTx(p)} title="Excluir cobrança" aria-label="Excluir cobrança" className="p-1.5 text-[#71717A] hover:text-[#F43F5E]"><Trash2 size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="equipe" className="mt-6">
          <TxTable lista={equipe} clientes={clientes} onToggle={baixa} onDelete={excluirTx} onEdit={abrirEdicaoTx} tipo="pagar" />
        </TabsContent>

        <TabsContent value="comercial" className="mt-6 space-y-6" data-testid="comercial-fin-content">
          {resumo?.comercial && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { id: "pipeline-pontual", label: "Pipeline pontual", valor: BRL(resumo.comercial.pipeline_pontual_valor), sub: `${resumo.comercial.pipeline_pontual_count} oportunidades · valor total` },
                { id: "pipeline-recorrente", label: "Pipeline recorrente", valor: `${BRL(resumo.comercial.pipeline_recorrente_valor_mensal)}/mês`, sub: `${resumo.comercial.pipeline_recorrente_count} oportunidades · receita mensal` },
                { id: "ganhos-pontuais-mes", label: "Pontual ganho no mês", valor: BRL(resumo.comercial.ganhos_mes_pontual_valor), sub: `${resumo.comercial.ganhos_mes_pontual_count} negócios` },
                { id: "novo-mrr-mes", label: "Novo MRR no mês", valor: `${BRL(resumo.comercial.ganhos_mes_recorrente_valor_mensal)}/mês`, sub: `${resumo.comercial.ganhos_mes_recorrente_count} negócios` },
                { id: "ganhos-total", label: "Negócios fechados", valor: resumo.comercial.ganhos_total, sub: "histórico total" },
                { id: "propostas", label: "Propostas abertas", valor: resumo.comercial.propostas_abertas, sub: "aguardando resposta" },
              ].map((c) => (
                <button key={c.id} data-testid={`fin-comercial-${c.id}`} onClick={() => navigate("/comercial")} title="Ver no Comercial"
                  className="studio-card studio-card-hover p-5 text-left cursor-pointer">
                  <p className="caption-label">{c.label}</p>
                  <p className="font-mono text-xl sm:text-2xl font-semibold mt-3 tracking-tight text-[#F4F4F0]">{c.valor}</p>
                  <p className="font-mono text-[10px] text-[#71717A] mt-2 uppercase">{c.sub}</p>
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <p className="caption-label">Propostas comerciais</p>
            {propostas.length === 0 && <p className="text-sm text-[#71717A]">Nenhuma proposta gerada ainda.</p>}
            {propostas.map((p) => {
              const status = p.status_efetivo || p.status;
              const statusLabel = { enviada: "aguardando decisão", aprovada: "aprovada", recusada: "recusada", substituida: "versão substituída", encerrada: "negócio encerrado", arquivada: "arquivada", sem_oportunidade: "sem oportunidade" }[status] || status;
              return (
              <div key={p.id} data-testid={`fin-proposta-${p.id}`} className="studio-card px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-[#E4E4E0]">{p.titulo}</p>
                  <p className="font-mono text-[10px] text-[#71717A] mt-0.5">{p.numero}</p>
                </div>
                <span className="font-mono text-sm text-[#8CC63F]">{BRL(p.total)}</span>
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${status === "aprovada" ? "text-[#10B981] border-[#10B981]/30" : ["recusada", "sem_oportunidade"].includes(status) ? "text-[#F43F5E] border-[#F43F5E]/30" : status === "enviada" ? "text-[#F59E0B] border-[#F59E0B]/30" : "text-[#71717A] border-[#71717A]/30"}`}>{statusLabel}</span>
              </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Baixa de recebimento ou pagamento */}
      <Dialog open={dialogBaixa} onOpenChange={setDialogBaixa}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Registrar <span className="gold-italic">{transacaoBaixa?.tipo === "receber" ? "recebimento" : "pagamento"}</span></DialogTitle></DialogHeader>
          {transacaoBaixa && (
            <form onSubmit={confirmarBaixa} className="space-y-4 mt-2">
              <div className="rounded-lg border border-[#27272A] bg-[#101014] p-4">
                <p className="text-sm text-[#E4E4E0]">{transacaoBaixa.descricao}</p>
                <p className="font-mono text-xs text-[#71717A] mt-1">Vencimento {fmtData(transacaoBaixa.data_vencimento)} · total {BRL(transacaoBaixa.valor)}</p>
                {Number(transacaoBaixa.valor_pago || 0) > 0 ? <p className="font-mono text-xs text-[#F59E0B] mt-1">Já {transacaoBaixa.tipo === "receber" ? "recebido" : "pago"} {BRL(transacaoBaixa.valor_pago)} · saldo {BRL(Math.max(transacaoBaixa.valor - transacaoBaixa.valor_pago, 0))}</p> : null}
              </div>
              <div>
                <Label className="caption-label">Tipo de baixa</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button data-testid="baixa-total-button" type="button" onClick={() => {
                    const saldo = Math.max(Number(transacaoBaixa.valor || 0) - Number(transacaoBaixa.valor_pago || 0), 0);
                    setModoBaixa("total");
                    setFormBaixa({ ...formBaixa, valor_pago: String(saldo || "") });
                  }} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${modoBaixa === "total" ? "border-[#8CC63F] bg-[#8CC63F]/10 text-[#8CC63F]" : "border-[#27272A] text-[#A1A1AA]"}`}>Valor total</button>
                  <button data-testid="baixa-parcial-button" type="button" onClick={() => {
                    setModoBaixa("parcial");
                    setFormBaixa({ ...formBaixa, valor_pago: "" });
                  }} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${modoBaixa === "parcial" ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#27272A] text-[#A1A1AA]"}`}>Valor parcial</button>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Data da baixa</Label>
                  <Input data-testid="baixa-data-input" type="date" required value={formBaixa.data_pagamento} onChange={(e) => setFormBaixa({ ...formBaixa, data_pagamento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Valor {transacaoBaixa.tipo === "receber" ? "recebido" : "pago"} agora</Label>
                  <Input data-testid="baixa-valor-input" type="number" min="0.01" max={Math.max(Number(transacaoBaixa.valor || 0) - Number(transacaoBaixa.valor_pago || 0), 0)} step="0.01" required value={formBaixa.valor_pago} onChange={(e) => setFormBaixa({ ...formBaixa, valor_pago: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              </div>
              {modoBaixa === "parcial" && <p className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-xs text-[#F59E0B]">Informe somente o valor desta baixa. O restante continuará em aberto para um próximo pagamento.</p>}
              <div><Label className="caption-label">Forma de pagamento</Label>
                <Select value={formBaixa.forma_pagamento} onValueChange={(v) => setFormBaixa({ ...formBaixa, forma_pagamento: v })}>
                  <SelectTrigger data-testid="baixa-forma-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="cartao">Cartão</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button data-testid="baixa-vencimento-button" type="button" onClick={confirmarBaixaNoVencimento} variant="outline" className="border-[#8CC63F]/50 text-[#8CC63F] hover:bg-[#8CC63F]/10 rounded-full font-semibold">Baixar no vencimento</Button>
                <Button data-testid="baixa-submit-button" type="submit" className="bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Confirmar baixa</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Novo lançamento */}
      <Dialog open={dialogTx} onOpenChange={setDialogTx}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editTxId ? "Editar" : "Novo"} <span className="gold-italic">lançamento</span></DialogTitle></DialogHeader>
          <form onSubmit={salvarTx} className="space-y-4 mt-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Tipo</Label>
                <Select value={formTx.tipo} onValueChange={(v) => setFormTx({ ...formTx, tipo: v })}>
                  <SelectTrigger data-testid="tx-tipo-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="receber">A receber</SelectItem>
                    <SelectItem value="pagar">A pagar</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="caption-label">Categoria</Label>
                <Select value={formTx.categoria} onValueChange={(v) => setFormTx({ ...formTx, categoria: v })}>
                  <SelectTrigger data-testid="tx-categoria-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="projeto">Projeto</SelectItem>
                    <SelectItem value="contrato">Contrato</SelectItem>
                    <SelectItem value="equipe">Equipe</SelectItem>
                    <SelectItem value="operacional">Operacional</SelectItem>
                    <SelectItem value="imposto">Imposto</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="caption-label">Descrição</Label>
              <Input data-testid="tx-descricao-input" required value={formTx.descricao} onChange={(e) => setFormTx({ ...formTx, descricao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Valor (R$)</Label>
                <Input data-testid="tx-valor-input" type="number" step="0.01" required value={formTx.valor} onChange={(e) => setFormTx({ ...formTx, valor: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Vencimento</Label>
                <Input data-testid="tx-vencimento-input" type="date" required value={formTx.data_vencimento} onChange={(e) => setFormTx({ ...formTx, data_vencimento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            {formTx.categoria === "equipe" && (
              <div><Label className="caption-label">Membro da equipe</Label>
                <Input data-testid="tx-membro-input" value={formTx.membro} onChange={(e) => setFormTx({ ...formTx, membro: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: João Editor" /></div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Forma de pagamento</Label>
                <Select value={formTx.forma_pagamento || "none"} onValueChange={(v) => setFormTx({ ...formTx, forma_pagamento: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="tx-forma-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="cartao">Cartão</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                  </SelectContent>
                </Select></div>
              {formTx.tipo === "pagar" ? (
                <div><Label className="caption-label">Fornecedor</Label>
                  <Input data-testid="tx-fornecedor-input" value={formTx.fornecedor} onChange={(e) => setFormTx({ ...formTx, fornecedor: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: locadora de lentes" /></div>
              ) : (
                <div><Label className="caption-label">Observações</Label>
                  <Input data-testid="tx-obs-input" value={formTx.observacoes} onChange={(e) => setFormTx({ ...formTx, observacoes: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              )}
            </div>
            {editTxId && ["recebido", "pago", "parcial"].includes(formTx.status) && (
              <div><div className="flex items-center justify-between gap-3"><Label className="caption-label">Data do pagamento</Label>
                <button type="button" data-testid="tx-pagamento-vencimento-button" onClick={corrigirPagamentoParaVencimento} className="font-mono text-[10px] uppercase tracking-wider text-[#8CC63F] hover:text-[#A9DD6B]">Corrigir pelo vencimento</button></div>
                <Input data-testid="tx-pagamento-input" type="date" required value={formTx.data_pagamento} onChange={(e) => setFormTx({ ...formTx, data_pagamento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            )}
            <div><Label className="caption-label">Cliente (opcional)</Label>
              <Select value={formTx.cliente_id} onValueChange={(v) => setFormTx({ ...formTx, cliente_id: v })}>
                <SelectTrigger data-testid="tx-cliente-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.empresa}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <Button data-testid="tx-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">{editTxId ? "Salvar alterações" : "Lançar"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Novo contrato */}
      <Dialog open={dialogCt} onOpenChange={setDialogCt}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editCtId ? "Editar" : "Contrato"} <span className="gold-italic">{editCtId ? "contrato" : "recorrente"}</span></DialogTitle></DialogHeader>
          <form onSubmit={salvarCt} className="space-y-4 mt-2">
            <div><Label className="caption-label">Título</Label>
              <Input data-testid="ct-titulo-input" required value={formCt.titulo} onChange={(e) => setFormCt({ ...formCt, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: Conteúdo recorrente — cliente X" /></div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="caption-label">Cliente</Label>
                {!editCtId && (
                  <button type="button" data-testid="ct-toggle-novo-cliente" onClick={() => setNovoCliente(!novoCliente)} className="font-mono text-[10px] uppercase tracking-wide text-[#8CC63F] hover:text-[#A9DD6B]">
                    {novoCliente ? "Selecionar existente" : "+ Novo cliente"}
                  </button>
                )}
              </div>
              {novoCliente ? (
                <div className="mt-1.5 border border-[#27272A] rounded-lg p-3 space-y-3" data-testid="ct-novo-cliente">
                  <Input data-testid="ct-cli-empresa" required value={formCli.empresa} onChange={(e) => setFormCli({ ...formCli, empresa: e.target.value })} placeholder="Empresa *" className="bg-[#18181C] border-[#27272A]" />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input data-testid="ct-cli-nome" value={formCli.nome} onChange={(e) => setFormCli({ ...formCli, nome: e.target.value })} placeholder="Contato" className="bg-[#18181C] border-[#27272A]" />
                    <Input data-testid="ct-cli-whatsapp" value={formCli.whatsapp} onChange={(e) => setFormCli({ ...formCli, whatsapp: e.target.value })} placeholder="WhatsApp" className="bg-[#18181C] border-[#27272A]" />
                    <Input data-testid="ct-cli-email" value={formCli.email} onChange={(e) => setFormCli({ ...formCli, email: e.target.value })} placeholder="E-mail" className="bg-[#18181C] border-[#27272A]" />
                    <Input data-testid="ct-cli-instagram" value={formCli.instagram} onChange={(e) => setFormCli({ ...formCli, instagram: e.target.value })} placeholder="Instagram (@)" className="bg-[#18181C] border-[#27272A]" />
                  </div>
                  <Input data-testid="ct-cli-site" value={formCli.site} onChange={(e) => setFormCli({ ...formCli, site: e.target.value })} placeholder="Site" className="bg-[#18181C] border-[#27272A]" />
                </div>
              ) : (
                <Select value={formCt.cliente_id} onValueChange={(v) => setFormCt({ ...formCt, cliente_id: v })}>
                  <SelectTrigger data-testid="ct-cliente-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.empresa}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div><Label className="caption-label">Formato do contrato</Label>
              <Select value={formCt.formato || "mensal"} onValueChange={(v) => setFormCt({ ...formCt, formato: v, frequencia_cobranca: formCt.frequencia_cobranca || "mensal" })}>
                <SelectTrigger data-testid="ct-formato-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {Object.entries(FORMATO_CT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="caption-label">Frequência das cobranças</Label>
              <Select value={formCt.frequencia_cobranca || "mensal"} onValueChange={(v) => setFormCt({ ...formCt, frequencia_cobranca: v, parcelas_por_mes: v === "semanal" ? (formCt.parcelas_por_mes || 4) : 1 })}>
                <SelectTrigger data-testid="ct-frequencia-cobranca-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formCt.formato === "pontual" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Valor total do projeto (R$)</Label>
                  <Input data-testid="ct-valor-total-input" type="number" min="0.01" step="0.01" required value={formCt.valor_total} onChange={(e) => setFormCt({ ...formCt, valor_total: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Quantidade de parcelas</Label>
                  <Input data-testid="ct-quantidade-parcelas-input" type="number" min="1" max="120" required value={formCt.quantidade_parcelas} onChange={(e) => setFormCt({ ...formCt, quantidade_parcelas: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Valor mensal do contrato (R$)</Label>
                  <Input data-testid="ct-valor-input" type="number" min="0.01" step="0.01" required value={formCt.valor_mensal} onChange={(e) => setFormCt({ ...formCt, valor_mensal: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                {formCt.frequencia_cobranca === "semanal" ? (
                  <div><Label className="caption-label">Cobranças por mês</Label>
                    <Input data-testid="ct-parcelas-mes-input" type="number" min="1" max="5" required value={formCt.parcelas_por_mes} onChange={(e) => setFormCt({ ...formCt, parcelas_por_mes: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                ) : (
                  <div><Label className="caption-label">Dia do vencimento</Label>
                    <Input data-testid="ct-dia-input" type="number" min="1" max="28" value={formCt.dia_vencimento} onChange={(e) => setFormCt({ ...formCt, dia_vencimento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                )}
              </div>
            )}
            {formCt.frequencia_cobranca === "semanal" && formCt.formato !== "pontual" ? (
              <div className="rounded-lg border border-[#8CC63F]/25 bg-[#8CC63F]/5 px-4 py-3">
                <p className="text-xs text-[#A1A1AA]">Cada mês gerará {Number(formCt.parcelas_por_mes || 1)} cobranças semanais de <span className="font-mono text-[#8CC63F]">{BRL((Number(formCt.valor_mensal || 0)) / Number(formCt.parcelas_por_mes || 1))}</span>, preservando o total mensal de {BRL(Number(formCt.valor_mensal || 0))}.</p>
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Início</Label>
                <Input data-testid="ct-inicio-input" type="date" required value={formCt.inicio} onChange={(e) => setFormCt({ ...formCt, inicio: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Emissão de NF</Label>
                <Select value={formCt.nf} onValueChange={(v) => setFormCt({ ...formCt, nf: v })}>
                  <SelectTrigger data-testid="ct-nf-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(NF_CT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="caption-label">Primeiro vencimento {formCt.formato === "pontual" || formCt.frequencia_cobranca === "semanal" ? "" : "(opcional)"}</Label>
              <Input data-testid="ct-primeiro-vencimento-input" type="date" required={formCt.formato === "pontual" || formCt.frequencia_cobranca === "semanal"} value={formCt.primeiro_vencimento} onChange={(e) => setFormCt({ ...formCt, primeiro_vencimento: e.target.value, dia_vencimento: e.target.value ? Number(e.target.value.slice(8, 10)) : formCt.dia_vencimento })} className="mt-1.5 bg-[#18181C] border-[#27272A]" />
              <p className="font-mono text-[10px] text-[#71717A] mt-1">As próximas cobranças serão calculadas automaticamente a partir desta data.{formCt.frequencia_cobranca === "semanal" && formCt.formato !== "pontual" ? " Para manter todas dentro do mês, escolha um dia entre 1 e 7." : ""}</p>
            </div>
            <div><Label className="caption-label">Fim (opcional — vazio = indeterminado)</Label>
              <Input data-testid="ct-fim-input" type="date" value={formCt.fim} onChange={(e) => setFormCt({ ...formCt, fim: e.target.value, renovacao_automatica: e.target.value ? formCt.renovacao_automatica : false })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <div className="rounded-xl border border-[#60A5FA]/25 bg-[#60A5FA]/5 p-4 space-y-4" data-testid="ct-assinatura-fluxo">
              <div>
                <p className="text-sm font-semibold text-[#E4E4E0]">Fluxo de aprovação e assinatura</p>
                <p className="mt-1 text-xs text-[#A1A1AA]">Atualize sempre o mesmo contrato. “Enviado” não significa “assinado”.</p>
              </div>
              <div><Label className="caption-label">Status da assinatura</Label>
                <Select value={formCt.assinatura_status || "rascunho"} onValueChange={(v) => setFormCt({ ...formCt, assinatura_status: v })}>
                  <SelectTrigger data-testid="ct-assinatura-status-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {Object.entries(ASSINATURA_CT).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="caption-label">Link no Autentique</Label>
                <Input data-testid="ct-autentique-url-input" type="url" value={formCt.autentique_url || ""} onChange={(e) => setFormCt({ ...formCt, autentique_url: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="https://..." />
              </div>
              <div><Label className="caption-label">ID do documento no Autentique (opcional)</Label>
                <Input data-testid="ct-autentique-id-input" value={formCt.autentique_documento_id || ""} onChange={(e) => setFormCt({ ...formCt, autentique_documento_id: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" />
              </div>
              {(formCt.aprovado_em || formCt.enviado_autentique_em || formCt.assinado_em) && (
                <p className="font-mono text-[10px] leading-relaxed text-[#71717A]">Aprovado: {formCt.aprovado_em ? fmtData(formCt.aprovado_em) : "—"} · Enviado: {formCt.enviado_autentique_em ? fmtData(formCt.enviado_autentique_em) : "—"} · Assinado: {formCt.assinado_em ? fmtData(formCt.assinado_em) : "—"}</p>
              )}
            </div>
            <div><Label className="caption-label">Link do contrato</Label>
              <Input data-testid="ct-contrato-url-input" type="url" value={formCt.contrato_url || ""} onChange={(e) => setFormCt({ ...formCt, contrato_url: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="https://drive.google.com/..." />
              <p className="font-mono text-[10px] text-[#71717A] mt-1">Cole o link do documento oficial no Drive ou na plataforma de assinatura.</p>
            </div>
            {formCt.formato !== "pontual" && formCt.fim && (
              <div className="space-y-4 rounded-xl border border-[#8CC63F]/25 bg-[#8CC63F]/5 p-4" data-testid="ct-renovacao-config">
                <label className="flex cursor-pointer items-start gap-3">
                  <input type="checkbox" checked={Boolean(formCt.renovacao_automatica)} onChange={(e) => setFormCt({ ...formCt, renovacao_automatica: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#8CC63F]" />
                  <span>
                    <span className="block text-sm font-semibold text-[#E4E4E0]">Renovação automática</span>
                    <span className="mt-1 block text-xs leading-relaxed text-[#A1A1AA]">Na data final, o contrato será prorrogado e as cobranças futuras serão criadas sem alterar pagamentos já realizados.</span>
                  </span>
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="caption-label">Renovar por quantos meses</Label>
                    <Input data-testid="ct-periodo-renovacao-input" type="number" min="1" max="120" value={formCt.periodo_renovacao_meses || ""} onChange={(e) => setFormCt({ ...formCt, periodo_renovacao_meses: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Mesma duração atual" /></div>
                  <div><Label className="caption-label">Avisar com antecedência</Label>
                    <div className="relative mt-1.5">
                      <Input data-testid="ct-aviso-vencimento-input" type="number" min="1" max="180" value={formCt.aviso_vencimento_dias} onChange={(e) => setFormCt({ ...formCt, aviso_vencimento_dias: e.target.value })} className="bg-[#18181C] border-[#27272A] pr-14" />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#71717A]">dias</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Button data-testid="ct-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">{editCtId ? "Salvar alterações" : "Criar contrato"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
